import { Tooltip } from "@mantine/core"
import { IconTrash } from "@tabler/icons-react"
import { ColumnDef, createColumnHelper } from "@tanstack/react-table"
import { ActionIcon, Select, TextCell } from "@tfg/ui"
import { useMemo } from "react"

import { OperationChain } from "@tfg/types/schemas/enum"
import { GetEcobalyseMaterialTypesResponseDto } from "@tfg/types/schemas/modules/lifeCycleAnalysis/dtos/get-ecobalyse-material-types.response.dto"

import { queryClient } from "~/backend/queryClient"
import { MaterialTypesResponseDto } from "~/gen/api"
import { useDeleteMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useDeleteMaterialTypeMutation"
import { useUpdateMaterialTypeMutation } from "~/pages/Admin/MasterLists/MaterialTypes/hooks/useUpdateMaterialTypeMutation"
import { showAxiosError } from "~/utils/showAxiosError"

import { EditableCellWithTextInput } from "../../Translations/columns"
import { useUpdateTranslationsMutation } from "../../Translations/hooks"

const columnHelper = createColumnHelper<MaterialTypesResponseDto>()
export const useColumns = (
  materialOpChains: OperationChain[],
  ecobalyseMaterialTypes: GetEcobalyseMaterialTypesResponseDto
) => {
  const { mutate: updateTranslation } = useUpdateTranslationsMutation({
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ["materialtypes"],
      }),
    onError: () =>
      showAxiosError({
        notificationTitle: "Error",
        notificationMessage: "Failed to update translation",
        showNotification: true,
        error: new Error("Failed to update translation"),
      }),
  })
  const { mutate: updateMaterialType } = useUpdateMaterialTypeMutation()
  const { mutate: deleteMaterialType } = useDeleteMaterialTypeMutation()

  return useMemo<ColumnDef<MaterialTypesResponseDto, any>[]>(
    () => [
      columnHelper.accessor("id", {
        header: "ID",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor("key", {
        header: "Key",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor("category", {
        header: "Category",
        cell: ({ getValue }) => <TextCell value={getValue()} />,
        enableSorting: false,
      }),
      columnHelper.accessor(
        (materialType) => {
          return materialType
        },
        {
          header: "Ecobalyse Id",
          enableSorting: false,
          cell: ({ getValue }) => {
            const materialType = getValue()

            return (
              <Select
                searchable
                clearable
                comboboxProps={{ width: "fit-content" }}
                defaultValue={materialType.ecobalyseId}
                data={ecobalyseMaterialTypes?.map((ecobalyseMaterialType) => ({
                  value: ecobalyseMaterialType.id,
                  label: ecobalyseMaterialType.name,
                }))}
                onChange={(newValue) => {
                  updateMaterialType({
                    materialId: materialType.id,
                    materialType: {
                      key: materialType.key,
                      category: materialType.category,
                      ecobalyseId: newValue,
                      operationChainId: materialType.materialOpChain.id,
                    },
                  })
                }}
              />
            )
          },
        }
      ),
      columnHelper.accessor((materialType) => materialType, {
        header: "materialOpChain",
        enableSorting: false,
        cell: ({ getValue }) => {
          const materialType = getValue()
          return (
            <Tooltip
              multiline
              maw={300}
              label="Changing this will have an impact on existing information requests, please contact tech team to change this."
            >
              <Select
                defaultValue={materialType.materialOpChain.id}
                data={Object.values(materialOpChains).map((chain) => ({
                  label: chain.name,
                  value: chain.id,
                }))}
                disabled={true}
                onChange={(newValue) => {
                  if (newValue === null) return
                  updateMaterialType({
                    materialId: materialType.id,
                    materialType: {
                      key: materialType.key,
                      category: materialType.category,
                      ecobalyseId: materialType.ecobalyseId,
                      operationChainId: newValue,
                    },
                  })
                }}
              />
            </Tooltip>
          )
        },
      }),
      columnHelper.accessor("EN", {
        header: "EN",
        enableSorting: false,
        cell: ({ row: { original: materialType }, getValue }) => (
          <EditableCellWithTextInput
            key={materialType.id}
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: materialType.typeTextContentId,
                translatedLanguage: "EN",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor("FR", {
        header: "FR",
        enableSorting: false,
        cell: ({ row: { original: materialType }, getValue }) => (
          <EditableCellWithTextInput
            key={materialType.id}
            defaultValue={getValue()}
            onSave={(newValue: string) => {
              updateTranslation({
                id: materialType.typeTextContentId,
                translatedLanguage: "FR",
                translation: newValue,
              })
            }}
          />
        ),
      }),
      columnHelper.accessor((row) => row.id, {
        header: () => null,
        id: "action-cell-delete",
        enableSorting: false,
        cell: ({ row }) => (
          <ActionIcon
            icon={<IconTrash />}
            onClick={() => deleteMaterialType(row.original.id)}
          />
        ),
      }),
    ],
    [
      updateTranslation,
      updateMaterialType,
      deleteMaterialType,
      ecobalyseMaterialTypes,
      materialOpChains,
    ]
  )
}
